import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CButton, CCard, CCardBody, CSmartTable } from '@coreui/react-pro';
import { MultiSelect } from 'react-multi-select-component';
import classNames from 'classnames';

import { callApi } from '../../../../../apiCaller';
import {
     API_SUBSCRIBER_ORGANIZATION_GET_BUNDLE_SEATS_FILTER,
     API_SUBSCRIBER_ORGANIZATION_TRAINING_GROUP_DETAIL,
     BUNDLE_SEAT_STATUS,
     DEFAULT_DATE_MONTH_NAME_TIME_FORMAT,
     DEFAULT_PAGINATION,
     DEFAULT_PAGINATION_SEAT_TABLE_INIT,
     INITIAL_STATE_PAGINATION,
     LOCAL_ORG_USER,
     POPUP_TYPE,
     SORTING_TYPE,
     TRAINING_GROUP_FORM,
     TRAINING_GROUP_OBJECT,
} from '../../../../../constants';
import {
     checkCeProductIsRPT,
     compareDateExpired,
     debounce,
     formatDate,
     generateId,
     getBundleSeatStatus,
     getSeatStatus,
     removeObjectValues,
     toastError,
     trimAllObjectValues,
     tryParseJSON,
} from '../../../../../utils';
import { useActiveProduct } from '../../../../../helpers/customHook';

import images from '../../../../../assets/images';
import { CreatePopup, DoubleConfirmRemovePopup } from '../../../../General/Popups';
import TooltipWithIcon from '../../../../General/Tooltips/TooltipWithIcon';
import { SimpleInputGroup, SimpleSelectGroup } from '../../../../General/Forms';
import Pagination from '../../../../General/Pagination';

import MultiSelectTrainingGroup from '../MultiSelectTrainingGroup';

import { setShowPopup, setShowPopupDetail, setShowRemovePopup } from '../../../../../actions/general';
import { removeBundleSeatRequest } from './actions';
import AssignMultipleUsers from './AssignMultipleUsers';
import AssignBundleSeat from './AssignBundleSeat';
import { addTrainingGroupUser, changeTrainingGroupUser, setOrganizationTrainingGroups } from '../../TrainingGroups/actions';
import TrainingGroupFormik from '../../TrainingGroups/TrainingGroup/TrainingGroupFormik';

const BundleSeatTable = () => {
     const dispatch = useDispatch();
     const { courseId } = useParams();

     const organizationCourses = useSelector((state) => state.subscriber.organizationCourses);
     const organizationCourse = organizationCourses?.find((orgCourse) => orgCourse.id === courseId);
     const products = useSelector((state) => state.subscriber.products.list);

     const productId = organizationCourse?.productId;
     const product = useActiveProduct(productId);

     const show = useSelector((state) => state.general.show);
     const removeLoading = useSelector((state) => state.general.removeLoading);
     const showPopupDetail = useSelector((state) => state.general.showPopupDetail);
     const showRemovePopup = useSelector((state) => state.general.showRemovePopup);
     const trainingGroups = useSelector((state) => state.subscriber.organizationTrainingGroups);
     const organizationMemberUsers = useSelector((state) => state.subscriber.organizationMembers);
     const showPopup = useSelector((state) => state.general.showPopup);

     const orgUserLocalStorage = tryParseJSON(localStorage.getItem(LOCAL_ORG_USER));
     const organizationId = orgUserLocalStorage?.organizationId;

     const [emailRemoveSeat, setEmailRemoveSeat] = useState('');
     const [listTrainingGroup, setListTrainingGroup] = useState([]);
     const [disableOptions, setDisableOptions] = useState(false);
     const [memberSelected, setMemberSelected] = useState(null);
     const [isLoadingTrainingGroups, setIsLoadingTrainingGroup] = useState(false);
     const [loadingCreatePopup, setLoadingCreatePopup] = useState(false);
     const [isLoadingTable, setIsLoadingTable] = useState(false);
     const [isLoadingChangeGroups, setIsLoadingChangeGroups] = useState(false);
     const [openSeats, setOpenSeats] = useState(-1);
     const [orgSeats, setOrgSeats] = useState(INITIAL_STATE_PAGINATION);
     const [pagination, setPagination] = useState(DEFAULT_PAGINATION_SEAT_TABLE_INIT);

     const isLoadingSeatTable = isLoadingTable || isLoadingTrainingGroups;

     const MANAGER_BUNDLE_SEAT_COLUMNS = [
          {
               key: 'code',
               label: 'Seat ID',
               _style: { minWidth: '113px', paddingLeft: '15px' },
               filter: () => {
                    return (
                         <SimpleInputGroup
                              id="seat-code"
                              className="mb-0"
                              value={pagination.filter.code ?? ''}
                              disabled={isLoadingSeatTable}
                              handleChange={(e) => {
                                   const filterCondition = {
                                        ...pagination.filter,
                                        code: e.target.value.trim(),
                                   };

                                   handleColumnFilter(filterCondition);
                              }}
                         />
                    );
               },
          },
          {
               key: 'firstName',
               label: 'First Name',
               _style: { minWidth: '135px' },
               filter: () => {
                    return (
                         <SimpleInputGroup
                              id="seat-first-name"
                              className="mb-0"
                              value={pagination.filter.firstName ?? ''}
                              disabled={isLoadingSeatTable}
                              handleChange={(e) => {
                                   const filterCondition = {
                                        ...pagination.filter,
                                        firstName: e.target.value,
                                   };

                                   handleColumnFilter(filterCondition);
                              }}
                         />
                    );
               },
          },
          {
               key: 'lastName',
               label: 'Last Name',
               _style: { minWidth: '135px' },
               filter: () => {
                    return (
                         <SimpleInputGroup
                              id="seat-last-name"
                              className="mb-0"
                              value={pagination.filter.lastName ?? ''}
                              disabled={isLoadingSeatTable}
                              handleChange={(e) => {
                                   const filterCondition = {
                                        ...pagination.filter,
                                        lastName: e.target.value,
                                   };

                                   handleColumnFilter(filterCondition);
                              }}
                         />
                    );
               },
          },
          {
               key: 'email',
               label: 'Email',
               _style: { width: '260px', minWidth: '195px' },
               filter: () => {
                    return (
                         <SimpleInputGroup
                              id="seat-email"
                              className="mb-0"
                              value={pagination.filter.email ?? ''}
                              disabled={isLoadingSeatTable}
                              handleChange={(e) => {
                                   const filterCondition = {
                                        ...pagination.filter,
                                        email: e.target.value.trim(),
                                   };

                                   handleColumnFilter(filterCondition);
                              }}
                         />
                    );
               },
          },
          {
               key: 'trainingGroup',
               label: 'Training Groups',
               _style: { width: '200px', minWidth: '220px' },
               sorter: false,
               filter: () => {
                    if (!trainingGroups) return <></>;

                    const handleChangeMultiGroups = (trainingGroupsValue) => {
                         const { trainingGroups, ...filterRest } = pagination.filter;
                         const trainingGroupsValues = trainingGroupsValue?.map((trainingGroup) => trainingGroup.value) || [];

                         setPagination((currentPagination) => ({
                              ...currentPagination,
                              page: DEFAULT_PAGINATION.PAGE,
                              filter: trainingGroupsValues.length
                                   ? {
                                          ...currentPagination.filter,
                                          trainingGroups: trainingGroupsValues,
                                     }
                                   : filterRest,
                         }));

                         const paginationPayload = {
                              ...pagination,
                              page: DEFAULT_PAGINATION.PAGE,
                              filter: trainingGroupsValues.length
                                   ? {
                                          ...pagination.filter,
                                          trainingGroups: trainingGroupsValues,
                                     }
                                   : filterRest,
                         };

                         debounceFilter(paginationPayload);
                    };

                    const groupsSelectedIds = pagination.filter.trainingGroups || [];
                    const selectedValues = listTrainingGroup.filter((option) => groupsSelectedIds.includes(option.value));

                    return (
                         <MultiSelect
                              options={listTrainingGroup}
                              value={selectedValues}
                              onChange={handleChangeMultiGroups}
                              labelledBy="Select"
                              className={classNames('multi-select', { 'no-option': isNoOption })}
                              disabled={disableOptions || isLoadingSeatTable}
                              overrideStrings={{ search: 'Type here to filter...' }}
                              onMenuToggle={(isOpen) => {
                                   if (isOpen) {
                                        const element = document.querySelector('.options');

                                        if (element?.scrollHeight > element?.clientHeight) {
                                             element.classList.add('is-scroll');
                                        } else {
                                             element.classList.remove('is-scroll');
                                        }
                                   }
                              }}
                         />
                    );
               },
          },
          {
               key: 'status',
               label: 'Status',
               _style: { minWidth: '110px', textAlign: 'center' },
               sorter: false,
               filter: () => {
                    const STATUS_OPTIONS = Object.values(BUNDLE_SEAT_STATUS).map((status) => {
                         const statusLabel = getSeatStatus(status);
                         return {
                              id: status,
                              name: statusLabel,
                         };
                    });

                    const handleChangeStatus = (e) => {
                         const statusValue = e.target.value;
                         const bundleSeatStatusValues = [];

                         const { seatStatus, ...filterRest } = pagination.filter;
                         if (statusValue) {
                              const BUNDLE_SEAT_STATUS_ARRAY = [BUNDLE_SEAT_STATUS.ACCEPTED, BUNDLE_SEAT_STATUS.OPEN, BUNDLE_SEAT_STATUS.RESERVED];

                              if (BUNDLE_SEAT_STATUS_ARRAY.includes(statusValue)) {
                                   bundleSeatStatusValues.push(statusValue);
                              }
                         }
                         setPagination((currentPagination) => ({
                              ...currentPagination,
                              page: DEFAULT_PAGINATION.PAGE,
                              filter:
                                   statusValue !== ''
                                        ? {
                                               ...currentPagination.filter,
                                               seatStatus: bundleSeatStatusValues,
                                          }
                                        : filterRest,
                         }));

                         const paginationPayload = {
                              ...pagination,
                              page: DEFAULT_PAGINATION.PAGE,
                              filter:
                                   statusValue !== ''
                                        ? {
                                               ...pagination.filter,
                                               seatStatus: bundleSeatStatusValues,
                                          }
                                        : filterRest,
                         };

                         debounceFilter(paginationPayload);
                    };
                    const selectedValue = pagination.filter.seatStatus?.[0];
                    return (
                         <SimpleSelectGroup
                              id="seat-status"
                              className="mb-0"
                              data={STATUS_OPTIONS}
                              value={selectedValue ?? ''}
                              handleChange={handleChangeStatus}
                              disabled={isLoadingSeatTable}
                         />
                    );
               },
          },
          {
               key: 'options',
               label: 'Action',
               _style: { minWidth: '150px', textAlign: 'center' },
               filter: false,
               sorter: false,
          },
     ];

     const fetchOrgTrainingGroups = () => {
          setIsLoadingTrainingGroup(true);
          callApi(API_SUBSCRIBER_ORGANIZATION_TRAINING_GROUP_DETAIL.replace(':organizationId', organizationId), 'GET')
               .then((response) => {
                    if (response.status !== 200) {
                         toastError(response);
                         return;
                    }

                    dispatch(setOrganizationTrainingGroups(response.data));
                    setListTrainingGroup(
                         response.data.map((tg) => ({
                              label: tg.name,
                              value: tg.id,
                         }))
                    );
               })
               .finally(() => {
                    setIsLoadingTrainingGroup(false);
               });
     };

     const fetchOrgSeats = (payload = pagination) => {
          if (!organizationId || !productId) {
               return;
          }

          const paginationPayload = {
               ...payload,
          };

          const newFilter = {};

          const isOrgIdChange = organizationId && payload.filter.organizationId !== organizationId;
          const isProductIdChange = productId && payload.filter.productId !== productId;

          if (isOrgIdChange) {
               Object.assign(newFilter, { organizationId });
          }

          if (isProductIdChange) {
               Object.assign(newFilter, { productId });
          }

          if (isOrgIdChange || isProductIdChange) {
               Object.assign(paginationPayload, {
                    filter: { ...payload.filter, ...newFilter },
               });

               setPagination(paginationPayload);
          }

          const { filter, page, pageSize, sortBy, sortType } = paginationPayload;
          const filterQuery = Object.keys(filter).length ? `&filter=${encodeURIComponent(JSON.stringify(filter))}` : '';
          const endpoint = `${API_SUBSCRIBER_ORGANIZATION_GET_BUNDLE_SEATS_FILTER}?page=${page}&pageSize=${pageSize}&sortBy=${sortBy}&sortType=${sortType}${filterQuery}`;

          setIsLoadingTable(true);

          callApi(endpoint, 'GET')
               .then((response) => {
                    if (response.status !== 200) {
                         toastError(response);
                         return;
                    }

                    setOrgSeats(response.data);
               })
               .finally(() => {
                    setIsLoadingTable(false);
               });
     };

     const fetchOpenSeats = () => {
          if (!organizationId || !productId) {
               return;
          }

          const paginationPayload = {
               ...DEFAULT_PAGINATION_SEAT_TABLE_INIT,
               filter: {
                    organizationId,
                    productId,
                    seatStatus: [BUNDLE_SEAT_STATUS.OPEN],
               },
          };
          const { filter, page, pageSize, sortBy, sortType } = paginationPayload;
          const filterQuery = `&filter=${encodeURIComponent(JSON.stringify(filter))}`;
          const endpoint = `${API_SUBSCRIBER_ORGANIZATION_GET_BUNDLE_SEATS_FILTER}?page=${page}&pageSize=${pageSize}&sortBy=${sortBy}&sortType=${sortType}${filterQuery}`;

          setOpenSeats(-1);
          callApi(endpoint, 'GET').then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    setOpenSeats(0);
                    return;
               }

               setOpenSeats(response.data.count);
          });
     };

     useEffect(() => {
          fetchOrgTrainingGroups();

          return () => {
               dispatch(setShowPopupDetail(null));
          };
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     useEffect(() => {
          fetchOrgSeats();
     }, [organizationId, productId]); // eslint-disable-line react-hooks/exhaustive-deps

     useEffect(() => {
          if (show === POPUP_TYPE.ASSIGN_MULTIPLE_USERS) {
               fetchOpenSeats();
          }
     }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

     const seats = useMemo(() => {
          if (!orgSeats.rows.length) return [];

          const seats = orgSeats.rows.map((orgSeat) => ({
               ...orgSeat,
               email: orgSeat.user?.email || orgSeat.email || '',
               firstName: orgSeat.user?.firstName || '',
               lastName: orgSeat.user?.lastName || '',
               role: orgSeat.user?.role || '',
               linkSelfId: orgSeat.link?.selfId || '',
          }));

          return seats;
     }, [orgSeats.rows]);

     // Filter array member not in seat and check duplicate email
     const organizationMembers = useMemo(() => {
          if (!organizationMemberUsers?.length) {
               return [];
          }

          const checkCeProduct = product && checkCeProductIsRPT(product, products);

          const filteredUsers = organizationMemberUsers.filter(
               (email) =>
                    !seats.some(
                         ({ email: seatEmail, expiredAt }) =>
                              email === seatEmail && (!expiredAt || new Date() <= new Date(expiredAt)) && !checkCeProduct
                    )
          );

          const uniqueUsers = Array.from(new Set(filteredUsers.map((user) => user.email))).map((email) => {
               const user = filteredUsers.find((u) => u.email === email);
               return user.id ? user : { ...user, id: generateId() };
          });

          return uniqueUsers;
     }, [organizationMemberUsers, seats, product, products]);

     const seatList = [...new Set(seats)];

     const handleShowAssignPopup = (seatId) => {
          dispatch(setShowPopupDetail(seatId));
     };

     const handleRemoveButtonClick = (id, userEmail) => {
          dispatch(setShowRemovePopup(id));
          setEmailRemoveSeat(userEmail);
     };

     // Handle remove seat
     const proceedToRemove = (seatId) => {
          const newValues = {
               email: emailRemoveSeat,
               organizationId,
               organizationProductId: courseId,
          };

          dispatch(removeBundleSeatRequest(newValues, seatId, fetchOrgSeats, setPagination));
     };

     // Handle change groups
     const handleChangeMultiSelect = useCallback(
          async (newSelected, seatMember, oldSelected = []) => {
               const itemsAdd = newSelected.filter((item) => !oldSelected.some((itemOld) => itemOld.value === item.value));
               const itemsRemove = oldSelected.filter((item) => !newSelected.some((itemNew) => itemNew.value === item.value));
               const valuesAdd = itemsAdd
                    .filter((item) => item.value !== 'addTrainingGroup')
                    .map((item) => ({
                         trainingGroupId: item.value,
                         userId: seatMember.userId,
                    }));
               const valuesRemove = itemsRemove.map((item) => ({
                    trainingGroupId: item.value,
                    userId: seatMember.userId,
               }));
               const payload = {
                    valuesAdd,
                    valuesRemove,
               };

               const nextDispatch = {
                    seatMember,
                    orgSeats,
                    setOrgSeats,
                    setDisableOptions,
                    setIsLoadingChangeGroups,
               };

               await dispatch(changeTrainingGroupUser(organizationId, payload, nextDispatch));
               if (pagination.filter.trainingGroups?.length) {
                    fetchOrgSeats();
               }
          },
          [orgSeats, organizationId] // eslint-disable-line react-hooks/exhaustive-deps
     );

     // Handle search groups
     const [isNoOption, setIsNoOptions] = useState(false);
     const handleFilterOptionsMultiselect = useCallback((options, filter) => {
          if (!filter) {
               setIsNoOptions(false);
               return options;
          }
          const re = new RegExp(filter, 'i');
          const optionsFilter = options.filter(({ label, value }) => {
               if (value === 'addTrainingGroup') return true;
               return label && label.match(re);
          });

          if (optionsFilter.length === 1) {
               setIsNoOptions(true);
          } else {
               setIsNoOptions(false);
          }

          return optionsFilter;
     }, []);

     // Handle add new group
     const handleButtonAddNew = useCallback((member) => {
          dispatch(setShowPopup());
          setMemberSelected(member);
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     const handleCreateGroup = (values, { setSubmitting }) => {
          const newValues = {
               ...values,
               organizationId,
               userId: memberSelected.userId,
          };

          const nextDispatch = {
               setSubmitting,
               listTrainingGroup,
               seatMember: memberSelected,
               setListTrainingGroup,
               handleChangeMultiSelect,
               setLoadingCreatePopup,
          };

          dispatch(addTrainingGroupUser(organizationId, newValues, nextDispatch));
     };

     // Handle change page
     const handleChangePage = useCallback(
          (e) => {
               const page = e.selected + 1;

               setPagination((currentPagination) => ({
                    ...currentPagination,
                    page,
               }));

               const paginationPayload = {
                    ...pagination,
                    page,
               };

               fetchOrgSeats(paginationPayload);
          },
          [pagination] // eslint-disable-line react-hooks/exhaustive-deps
     );

     // Handle change custom page
     const handleChangeCustomPage = (page) => {
          setPagination((currentPagination) => ({
               ...currentPagination,
               page,
          }));

          const paginationPayload = {
               ...pagination,
               page,
          };

          fetchOrgSeats(paginationPayload);
     };

     // Handle change item per page
     const handleChangeItemsPerPage = (pageSize) => {
          if (pagination.pageSize === pageSize) {
               return;
          }

          setPagination((currentPagination) => ({
               ...currentPagination,
               page: DEFAULT_PAGINATION.PAGE,
               pageSize,
          }));

          const paginationPayload = {
               ...pagination,
               page: DEFAULT_PAGINATION.PAGE,
               pageSize,
          };

          fetchOrgSeats(paginationPayload);
     };

     // Handle sort column
     const handleChangeSort = (e) => {
          let sortBy = e.column;
          let sortType = e.state;

          if (!Object.keys(e).length || (pagination.sortBy === sortBy && pagination.sortType === sortType)) {
               return;
          }

          const sortByObj = {
               email: 'emailInvite',
          };

          sortBy = sortByObj[sortBy] ?? sortBy;

          if (sortType === 0) {
               sortBy = 'reservedAt';
               sortType = SORTING_TYPE.DESC;
          }

          setPagination((currentPagination) => ({
               ...currentPagination,
               sortBy,
               sortType,
          }));

          const paginationPayload = {
               ...pagination,
               sortBy,
               sortType,
          };

          fetchOrgSeats(paginationPayload);
     };

     // Handle filter seat
     const debounceFilter = useMemo(() => {
          return debounce((payload) => {
               fetchOrgSeats(payload);
          }, 1000);
     }, [organizationId, productId]); // eslint-disable-line react-hooks/exhaustive-deps

     const handleColumnFilter = (values) => {
          if (isLoadingTable || isLoadingTrainingGroups) {
               return;
          }

          const filter = removeObjectValues({
               ...pagination.filter,
               ...values,
          });

          if (!Object.keys(pagination.filter).length && !Object.keys(filter).length) {
               return;
          }

          setPagination((currentPagination) => ({
               ...currentPagination,
               page: DEFAULT_PAGINATION.PAGE,
               filter,
          }));

          const correctFilter = trimAllObjectValues(filter);

          const paginationPayload = {
               ...pagination,
               page: DEFAULT_PAGINATION.PAGE,
               filter: correctFilter,
          };

          debounceFilter(paginationPayload);
     };

     return (
          <>
               {show === POPUP_TYPE.ASSIGN_MULTIPLE_USERS && (
                    <AssignMultipleUsers
                         seats={seatList}
                         productId={productId}
                         openSeats={openSeats}
                         setPagination={setPagination}
                         fetchOrgSeats={fetchOrgSeats}
                    />
               )}

               {showPopupDetail && (
                    <AssignBundleSeat
                         productId={productId}
                         bundleSeatId={showPopupDetail}
                         bundleSeats={seatList}
                         organizationMembers={organizationMembers}
                         fetchOrgSeats={fetchOrgSeats}
                         setPagination={setPagination}
                    />
               )}

               {showRemovePopup && <DoubleConfirmRemovePopup onAccept={proceedToRemove} id={showRemovePopup} loading={removeLoading} />}

               {showPopup && (
                    <CreatePopup
                         object={TRAINING_GROUP_OBJECT}
                         formId={TRAINING_GROUP_FORM}
                         className="invite-user-owner-popup"
                         options={{ oneField: true, colorCancel: 'light' }}
                         textSubmit="Save change"
                         disabled={loadingCreatePopup}
                    >
                         <div className="invite-owner-description">
                              <h2 className="h4 pb-2">Create a Group to Organize Your Training</h2>
                              <div className="invite-user-form">
                                   <TrainingGroupFormik initialValues={{ name: '' }} handleCreateGroup={handleCreateGroup} />
                              </div>
                         </div>
                    </CreatePopup>
               )}

               <CCard className="table-data table-large">
                    <div className={classNames('table-responsive middle-table table-manage-seat', { 'lazy-loading disabled-table': isLoadingTable })}>
                         <CCardBody>
                              <CSmartTable
                                   clickableRows
                                   items={seatList}
                                   columns={MANAGER_BUNDLE_SEAT_COLUMNS}
                                   loading={isLoadingSeatTable}
                                   noItemsLabel={isLoadingTable ? 'Loading...' : !seatList.length ? 'No items found' : ''}
                                   itemsPerPageSelect
                                   itemsPerPage={pagination.pageSize}
                                   columnSorter={{ external: true, resetable: true }}
                                   columnFilter={{ external: true, lazy: true }}
                                   onSorterChange={handleChangeSort}
                                   onColumnFilterChange={handleColumnFilter}
                                   onItemsPerPageChange={handleChangeItemsPerPage}
                                   scopedColumns={{
                                        code: (item) => (
                                             <td>
                                                  <TooltipWithIcon
                                                       srcIcon={images.iconInfoGray}
                                                       content={<div dangerouslySetInnerHTML={{ __html: item.code }} />}
                                                       trigger="click"
                                                  />
                                             </td>
                                        ),
                                        email: (item) => <td className="email">{item.email}</td>,
                                        trainingGroup: (item) => {
                                             return (
                                                  <td>
                                                       <MultiSelectTrainingGroup
                                                            item={item}
                                                            listTrainingGroup={listTrainingGroup}
                                                            isNoOption={isNoOption}
                                                            disableOptions={disableOptions}
                                                            isLoadingSeatTable={isLoadingSeatTable}
                                                            isLoadingChangeGroups={isLoadingChangeGroups}
                                                            handleButtonAddNew={handleButtonAddNew}
                                                            handleChangeMultiSelect={handleChangeMultiSelect}
                                                            handleFilterOptionsMultiselect={handleFilterOptionsMultiselect}
                                                       />
                                                  </td>
                                             );
                                        },
                                        status: (item) => {
                                             const reservationExpiredAt = item.reservationExpiredAt;
                                             const seatIsExpired =
                                                  item.status === BUNDLE_SEAT_STATUS.RESERVED && compareDateExpired(reservationExpiredAt);
                                             const status = item.status;

                                             return (
                                                  <td className="status">
                                                       <span className={status}>{getBundleSeatStatus(status)}</span>

                                                       {seatIsExpired && (
                                                            <>
                                                                 <br />
                                                                 <span className="text-danger text-expired">
                                                                      Reservation Expired on:{' '}
                                                                      {formatDate({
                                                                           date: item.reservationExpiredAt,
                                                                           format: DEFAULT_DATE_MONTH_NAME_TIME_FORMAT,
                                                                           useTimezone: false,
                                                                      })}
                                                                 </span>
                                                            </>
                                                       )}
                                                  </td>
                                             );
                                        },
                                        options: (item) => {
                                             const filledStatus = item.status === BUNDLE_SEAT_STATUS.FILLED;

                                             const isShowRemoveSeat = item.status === BUNDLE_SEAT_STATUS.RESERVED;

                                             const disabledRemoveSeat = filledStatus || isLoadingSeatTable;

                                             return (
                                                  <td className="actions text-center">
                                                       {!item.userId && item.status === BUNDLE_SEAT_STATUS.OPEN && (
                                                            <CButton
                                                                 className="btn-arrow-right"
                                                                 onClick={() => handleShowAssignPopup(item.id)}
                                                                 disabled={isLoadingSeatTable}
                                                            >
                                                                 Assign Seat
                                                            </CButton>
                                                       )}

                                                       {isShowRemoveSeat && (
                                                            <CButton
                                                                 color="danger"
                                                                 className="btn-arrow-right"
                                                                 disabled={disabledRemoveSeat}
                                                                 onClick={() => handleRemoveButtonClick(item.id, item.email)}
                                                            >
                                                                 Remove User
                                                            </CButton>
                                                       )}
                                                  </td>
                                             );
                                        },
                                   }}
                              />
                         </CCardBody>
                    </div>

                    <Pagination
                         currentPage={pagination.page}
                         pageSize={pagination.pageSize}
                         pageCount={orgSeats.count}
                         disabled={isLoadingSeatTable}
                         onChangePage={handleChangePage}
                         onChangeCustomPage={handleChangeCustomPage}
                    />
               </CCard>
          </>
     );
};

export default memo(BundleSeatTable);
